import type {
  Cell,
  Column,
  HeaderGroup,
  Row,
  SortDirection,
} from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type {
  CSSProperties,
  HTMLAttributes,
  MouseEvent,
  ReactElement,
  Ref,
  RefObject,
} from 'react';
import {
  isValidElement,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Icon } from 'semantic-ui-react';

import EmptyPlaceholder, {
  type EmptyPlaceholderProps,
} from '@components/empty-placeholder';
import {
  type InfiniteScrollOptions,
  useVirtualRows,
  type VirtualWindowOptions,
} from '@components/virtual-list';
import { getOffsetTopWithin } from '@utils/dom-utils';

import ColumnDescription from './column-description';
import type {
  NoDataContent,
  TableRowOptions,
  VirtualRowsHandle,
} from './types';
import s from './table-view.scss';

export interface TableCellProps<TData> {
  /**
   * The cell to render.
   */
  cell: Cell<TData, unknown>;
  /**
   * The resolved width style for the cell's column.
   */
  style: CSSProperties;
}

/**
 * Renders one body cell: the `role="cell"` wrapper with the column's
 * `meta.className` and resolved width, and the cell's rendered content.
 */
export const TableCell = <TData,>({ cell, style }: TableCellProps<TData>) => (
  <div
    className={classNames(s.td, cell.column.columnDef.meta?.className)}
    role="cell"
    style={style}
  >
    <div className={s.tdContent}>
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  </div>
);

export interface TableRowProps<TData> extends TableRowOptions<TData> {
  /**
   * Resolves the width style for a column.
   */
  getColumnStyle: (column: Column<TData>) => CSSProperties;
  /**
   * The row to render.
   */
  row: Row<TData>;
  /**
   * Whether the row takes the striped background.
   */
  striped: boolean;
}

// Roles and `label` as well as tags: Semantic gives its dropdowns a role but its
// Checkbox none, so a click on the label or its padding only matches `label`.
// `Element`, not `HTMLElement`: clicking an inline SVG icon targets the svg.
const INTERACTIVE_SELECTOR =
  'a, button, input, label, select, textarea, [role="button"], [role="checkbox"], [role="combobox"], [role="link"], [role="listbox"], [role="menuitem"], [role="switch"], [role="textbox"]';

// Bails a click from a nested control, so the row doesn't double-fire its action.
const isFromInteractiveElement = (event: MouseEvent<HTMLDivElement>) =>
  (event.target as Element).closest(INTERACTIVE_SELECTOR) !== null;

/**
 * Renders one body row and its cells, appending the expanded content beneath
 * the row while it is expanded.
 */
export const TableRow = <TData,>({
  expandedRow,
  getColumnStyle,
  isRowActive,
  onRowClick,
  row,
  rowClassName,
  striped,
}: TableRowProps<TData>) => (
  <>
    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- rows deliberately have no keyboard affordance, see MIGRATION.md's known gaps */}
    <div
      aria-current={isRowActive?.(row) ? 'true' : undefined}
      className={classNames(
        s.tr,
        striped && s.striped,
        onRowClick && s.clickable,
        isRowActive?.(row) && s.active,
        rowClassName?.(row),
      )}
      onClick={
        onRowClick
          ? event => {
              if (isFromInteractiveElement(event)) return;
              onRowClick(row, event);
            }
          : undefined
      }
      role="row"
    >
      {row.getVisibleCells().map(cell => (
        <TableCell
          key={cell.id}
          cell={cell}
          style={getColumnStyle(cell.column)}
        />
      ))}
    </div>

    {row.getIsExpanded() && expandedRow ? expandedRow(row) : null}
  </>
);

type ResizeHandleProps = Pick<
  HTMLAttributes<HTMLDivElement>,
  'onClick' | 'onMouseDown'
>;

export interface TableHeadProps<TData> {
  /**
   * Resolves the width style for a column.
   */
  getColumnStyle: (column: Column<TData>) => CSSProperties;
  /**
   * Resolves the resize-grip props for a column, or nothing when it has no grip.
   */
  getResizeHandleProps: (columnId: string) => ResizeHandleProps | undefined;
  /**
   * The table's header groups.
   */
  headerGroups: HeaderGroup<TData>[];
  /**
   * Whether a column is resizable, which the rightmost resizable one is despite
   * carrying no grip.
   */
  isColumnResizable: (columnId: string) => boolean;
  /**
   * Whether the header is pinned to the sticky boundary.
   */
  isPinned: boolean;
}

const renderSortIcon = (sorted: SortDirection | false): ReactElement => (
  <Icon.Group className={s.sortIcon} size="large">
    <Icon color={sorted === 'asc' ? 'teal' : 'grey'} name="sort up" />
    <Icon color={sorted === 'desc' ? 'teal' : 'grey'} name="sort down" />
  </Icon.Group>
);

/**
 * Renders the header region: one row per header group, each column's sort
 * affordance and icons, description tooltip, and resize grip.
 */
export const TableHead = <TData,>({
  getColumnStyle,
  getResizeHandleProps,
  headerGroups,
  isColumnResizable,
  isPinned,
}: TableHeadProps<TData>) => {
  useStyles(s);

  return (
    <div className={classNames(s.thead, isPinned && s.pinned)} role="rowgroup">
      {headerGroups.map(headerGroup => (
        <div key={headerGroup.id} className={s.tr} role="row">
          {headerGroup.headers.map(header => {
            const { column } = header;
            const { meta } = column.columnDef;
            const canSort = column.getCanSort();
            const sorted = column.getIsSorted();
            const toggleSort = column.getToggleSortingHandler();
            const resizeHandleProps = getResizeHandleProps(column.id);

            const content = (
              <div
                aria-sort={
                  sorted
                    ? sorted === 'desc'
                      ? 'descending'
                      : 'ascending'
                    : canSort
                      ? 'none'
                      : undefined
                }
                className={classNames(
                  s.th,
                  canSort && s.sortable,
                  isColumnResizable(column.id) && s.resizable,
                  meta?.headerClassName,
                )}
                onClick={canSort ? toggleSort : undefined}
                onKeyDown={
                  canSort
                    ? event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleSort?.(event);
                        }
                      }
                    : undefined
                }
                role="columnheader"
                style={getColumnStyle(column)}
                tabIndex={canSort || meta?.description ? 0 : undefined}
              >
                {canSort ? renderSortIcon(sorted) : null}

                <div className={s.thContent}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(column.columnDef.header, header.getContext())}
                </div>

                {resizeHandleProps ? (
                  <div
                    className={s.resizeHandle}
                    role="presentation"
                    {...resizeHandleProps}
                  />
                ) : null}
              </div>
            );

            return (
              <ColumnDescription
                key={header.id}
                description={meta?.description}
                trigger={content}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

export interface TableEmptyStateProps {
  /**
   * Whether an active filter is applied, selecting the filtered empty variant.
   */
  filtering: boolean;
  /**
   * Empty-state content: default-placeholder props, a node, or a render function.
   */
  noData?: NoDataContent;
}

/**
 * Resolves `noData` into the empty state, rendered outside the `role="table"`
 * element that may only contain row groups.
 */
export const TableEmptyState = ({
  filtering,
  noData,
}: TableEmptyStateProps) => {
  const isNode =
    noData != null &&
    typeof noData !== 'function' &&
    (isValidElement(noData) ||
      Array.isArray(noData) ||
      typeof noData !== 'object');

  if (typeof noData === 'function') return noData({ filtering });
  if (isNode) return noData;
  // `null` is a ReactNode meaning "render nothing"; only an absent prop falls
  // through to the default placeholder.
  if (noData === null) return null;

  const placeholderProps = noData as
    Omit<EmptyPlaceholderProps, 'filtering'> | undefined;

  return (
    <EmptyPlaceholder
      itemName="item"
      {...placeholderProps}
      filtering={filtering}
    />
  );
};

/**
 * Rows rendered past the current window before load-more fires, when no
 * explicit threshold is given.
 */
const DEFAULT_LOAD_MORE_THRESHOLD = 8;

export interface TableBodyProps<TData> extends TableRowOptions<TData> {
  /**
   * Resolves the width style for a column.
   */
  getColumnStyle: (column: Column<TData>) => CSSProperties;
  /**
   * Fetches more rows as the windowed tail is reached; enables virtualization.
   */
  infinite?: InfiniteScrollOptions;
  /**
   * Table root the sticky chrome above the body is measured against.
   */
  rootRef: RefObject<HTMLElement>;
  /**
   * The rows to render.
   */
  rows: Row<TData>[];
  /**
   * The sticky offset the windowed scroller pads its start by.
   */
  stickyInsetTop: number;
  /**
   * Ref populated with the row virtualizer's imperative scroll handle.
   */
  virtualHandleRef?: Ref<VirtualRowsHandle>;
  /**
   * Whether the body is windowed, optionally with window tuning options.
   */
  virtualized?: boolean | VirtualWindowOptions;
}

/**
 * Renders the table's rows, plain or windowed, owning the row virtualizer and
 * the infinite load-more trigger.
 */
export const TableBody = <TData,>({
  expandedRow,
  getColumnStyle,
  infinite,
  isRowActive,
  onRowClick,
  rootRef,
  rowClassName,
  rows,
  stickyInsetTop,
  virtualHandleRef,
  virtualized,
}: TableBodyProps<TData>) => {
  useStyles(s);

  const tbodyRef = useRef<HTMLDivElement>(null);

  const isVirtualized = Boolean(virtualized) || Boolean(infinite);
  const virtualOptions =
    typeof virtualized === 'object' ? virtualized : undefined;

  // The pager and column header stick inside the table, so scrolling a row to
  // the top has to clear them as well as the page-level inset. Keyed on row
  // presence rather than count, so appends don't force a reflow read per fetch.
  const [stickyChromeHeight, setStickyChromeHeight] = useState(0);
  const hasRows = rows.length > 0;

  useEffect(() => {
    if (!isVirtualized) return;

    // Floored at zero: this is padding for the chrome above the body, and a
    // negative would push a scrolled-to row back under it.
    const offset = Math.max(
      0,
      getOffsetTopWithin(tbodyRef.current, rootRef.current),
    );

    setStickyChromeHeight(prev => (prev === offset ? prev : offset));
  }, [hasRows, isVirtualized, rootRef]);

  const {
    measureElement,
    scrollMargin,
    scrollToIndex,
    totalSize,
    virtualItems,
  } = useVirtualRows({
    count: rows.length,
    enabled: isVirtualized,
    // Match the 60px row min-height so the first paint doesn't under-estimate
    // and reflow (the hook's own default is 44).
    estimateSize: virtualOptions?.estimateSize ?? 60,
    overscan: virtualOptions?.overscan,
    scrollMarginRef: tbodyRef,
    scrollPaddingStart: stickyInsetTop + stickyChromeHeight,
  });

  useImperativeHandle(virtualHandleRef, () => ({ scrollToIndex }), [
    scrollToIndex,
  ]);

  const hasMoreInfiniteRows = infinite?.hasMore ?? false;
  const isLoadingInfiniteRows = infinite?.isLoading ?? false;
  const loadMore = infinite?.loadMore;
  const loadMoreThreshold =
    infinite?.loadMoreThreshold ?? DEFAULT_LOAD_MORE_THRESHOLD;

  // Request at most once per row count so a consumer whose `isLoading` lags a
  // render can't re-fire the fetch.
  const requestedAtRowCountRef = useRef(-1);
  const leadingRowRef = useRef<unknown>(undefined);

  // The leading row survives an append but not a replacement, which can land on
  // the same count the last request was made at and wedge the latch for good.
  // Comparing identity is enough only while `infinite` reaches this from
  // ServerDataTable alone — an in-memory re-sort reorders the same objects.
  const leadingRow = rows[0]?.original;

  useEffect(() => {
    if (leadingRowRef.current !== leadingRow) {
      leadingRowRef.current = leadingRow;
      requestedAtRowCountRef.current = -1;
    }

    if (!loadMore || !hasMoreInfiniteRows || isLoadingInfiniteRows) return;
    if (requestedAtRowCountRef.current === rows.length) return;

    const lastItem = virtualItems.at(-1);
    if (lastItem && lastItem.index >= rows.length - 1 - loadMoreThreshold) {
      requestedAtRowCountRef.current = rows.length;
      loadMore();
    }
  }, [
    hasMoreInfiniteRows,
    isLoadingInfiniteRows,
    leadingRow,
    loadMore,
    loadMoreThreshold,
    rows.length,
    virtualItems,
  ]);

  if (isVirtualized) {
    return (
      <div
        ref={tbodyRef}
        className={s.virtualBody}
        role="rowgroup"
        style={{ height: totalSize }}
      >
        {virtualItems.map(virtualItem => (
          <div
            key={virtualItem.key}
            ref={measureElement}
            className={classNames(
              s.virtualRow,
              virtualItem.index === rows.length - 1 && s.virtualRowLast,
            )}
            data-index={virtualItem.index}
            // Transparent in the a11y tree so the inner role="row" sits under the rowgroup.
            role="presentation"
            style={{ top: virtualItem.start - scrollMargin }}
          >
            <TableRow
              expandedRow={expandedRow}
              getColumnStyle={getColumnStyle}
              isRowActive={isRowActive}
              onRowClick={onRowClick}
              row={rows[virtualItem.index]}
              rowClassName={rowClassName}
              striped={virtualItem.index % 2 === 1}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={s.tbody} role="rowgroup">
      {rows.map((row, rowIndex) => (
        <TableRow
          key={row.id}
          expandedRow={expandedRow}
          getColumnStyle={getColumnStyle}
          isRowActive={isRowActive}
          onRowClick={onRowClick}
          row={row}
          rowClassName={rowClassName}
          striped={rowIndex % 2 === 1}
        />
      ))}
    </div>
  );
};
